import { useAccountInfo } from '../api/queryHooks/account';
import { SHOP_STATES, type ShopState } from '../api/types';
import { useLogin } from './useLogin';
import { isStaff } from '../auth/roles';

export interface UseShopStateResult {
  /** The shop's state from accounts/info; missing or unknown means `open`. */
  state: ShopState;
  /** Staff, or an admin impersonating a customer: the state never holds them
   *  back (the backend lets both through, see UserAuthStateCache.apply). */
  isStaffView: boolean;
  /** The shop is closed and this visitor is held out of it. */
  closedForMe: boolean;
  /** The shop is not taking orders and this visitor may not browse albums,
   *  change the cart, check out or pay. */
  pausedForMe: boolean;
  isPending: boolean;
}

export const readShopState = (value: unknown): ShopState =>
  (SHOP_STATES as readonly unknown[]).includes(value) ? (value as ShopState) : 'open';

// Shop state (docs/superpowers/specs/2026-10-22-remove-sandbox-mode-design.md §6).
// The backend enforces it; this only keeps the UI from offering what the
// backend refuses.
export const useShopState = (): UseShopStateResult => {
  const { data: accountInfo, isPending: accountPending } = useAccountInfo();
  const { userData, impersonation, isPending: loginPending } = useLogin();

  const state = readShopState(accountInfo?.shop_state);
  const isStaffView = isStaff(userData?.roles) || Boolean(impersonation);

  return {
    state,
    isStaffView,
    closedForMe: state === 'closed' && !isStaffView,
    pausedForMe: state === 'orders_paused' && !isStaffView,
    isPending: accountPending || loginPending,
  };
};
